import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiDownload, FiRefreshCw, FiPackage, FiFileText,
  FiAlertTriangle, FiShield, FiZap, FiTarget,
} = FiIcons;

const StatBadge = ({ label, value, color }) => (
  <div className="flex flex-col items-center px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50">
    <span className={`text-lg font-bold ${color}`}>{value}</span>
    <span className="text-xs text-slate-500 mt-0.5 text-center">{label}</span>
  </div>
);

const BulkResultCard = ({ result, onReset }) => {
  if (!result) return null;

  const {
    zipUrl, zipFilename, successCount, failCount,
    pageResults, totalChanges, trueUniqueCount, angle,
  } = result;

  const uniquePct = successCount > 0 ? Math.round((trueUniqueCount / successCount) * 100) : 0;

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-green-500/20 rounded-xl shadow-2xl mt-4 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-800/60 flex items-center gap-3">
        <div className="p-2 bg-green-500/15 rounded-lg">
          <SafeIcon icon={FiPackage} className="text-green-400 text-lg" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-white">Bulk Build Complete</h3>
          <p className="text-xs text-slate-500">
            {successCount} pages · 2 LLM calls · spintax engine
          </p>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-4 gap-2 mb-5">
          <StatBadge label="Pages Built" value={successCount} color="text-green-400" />
          <StatBadge label="Failed" value={failCount} color={failCount > 0 ? 'text-red-400' : 'text-slate-500'} />
          <StatBadge label="Transforms" value={totalChanges} color="text-indigo-400" />
          <StatBadge label="Unique %" value={`${uniquePct}%`} color={uniquePct >= 95 ? 'text-green-400' : 'text-yellow-400'} />
        </div>

        {/* Angle used */}
        {angle && (
          <div className="mb-4 p-3 bg-violet-950/20 border border-violet-800/30 rounded-lg">
            <div className="flex items-center gap-2 mb-1.5">
              <SafeIcon icon={FiTarget} className="text-violet-400 text-sm shrink-0" />
              <p className="text-xs font-semibold text-violet-300">Content Angle Used</p>
              <span className="ml-auto text-xs text-slate-600 font-mono">2 total LLM calls</span>
            </div>
            <p className="text-xs text-slate-300 font-mono italic">"{angle}"</p>
          </div>
        )}

        {/* Cost badge */}
        <div className="mb-4 p-3 bg-green-950/20 border border-green-800/30 rounded-lg flex items-center gap-3">
          <SafeIcon icon={FiZap} className="text-green-400 text-sm shrink-0" />
          <p className="text-xs text-green-300 font-semibold">
            Only 2 LLM calls made — regardless of page count. Spintax resolved client-side.
          </p>
        </div>

        {/* Uniqueness banner */}
        <div className={`flex items-center gap-3 p-3 rounded-lg border mb-4 ${
          uniquePct >= 95
            ? 'bg-green-950/20 border-green-800/30'
            : 'bg-yellow-950/20 border-yellow-700/30'
        }`}>
          <SafeIcon
            icon={uniquePct >= 95 ? FiShield : FiAlertTriangle}
            className={`shrink-0 ${uniquePct >= 95 ? 'text-green-400' : 'text-yellow-400'}`}
          />
          <p className={`text-xs font-semibold ${uniquePct >= 95 ? 'text-green-300' : 'text-yellow-300'}`}>
            {uniquePct >= 95
              ? `✓ ${trueUniqueCount}/${successCount} pages have unique content fingerprints`
              : `${trueUniqueCount}/${successCount} pages unique (${uniquePct}%) — spintax may need more variety`}
          </p>
        </div>

        {failCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-yellow-900/20 border border-yellow-700/30 rounded-lg mb-4">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-400 shrink-0" />
            <p className="text-xs text-yellow-300">
              {failCount} page(s) failed. Check the terminal log for details.
            </p>
          </div>
        )}

        <div className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/40 mb-4">
          <p className="text-xs text-slate-500 font-mono truncate">→ {zipFilename}</p>
        </div>

        {/* Page list preview */}
        <div className="mb-5 max-h-36 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/50">
          {pageResults.slice(0, 20).map((page, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-800/50 last:border-0">
              <SafeIcon icon={FiFileText} className="text-slate-600 text-xs shrink-0" />
              <span className="text-xs font-mono text-slate-400 truncate flex-1">{page.filename}</span>
              <span className={`text-xs font-mono shrink-0 ${page.unique ? 'text-green-500' : 'text-yellow-500'}`}>
                {page.unique ? '✓ unique' : '⚠ dup'}
              </span>
              <span className="text-xs font-mono text-slate-600 shrink-0">{page.changes}x</span>
            </div>
          ))}
          {pageResults.length > 20 && (
            <div className="px-3 py-1.5 text-xs text-slate-600 italic">
              ...and {pageResults.length - 20} more files in ZIP
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <a
            href={zipUrl}
            download={zipFilename}
            className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-lg shadow-indigo-500/20 active:scale-[0.98]"
          >
            <SafeIcon icon={FiDownload} />
            Download ZIP ({successCount} pages)
          </a>
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm border border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white transition-all"
          >
            <SafeIcon icon={FiRefreshCw} className="text-sm" />
            New Build
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkResultCard;